interface OAuthProviderItf {
  name: string;
  can_signup: boolean;
  can_login: boolean;
}

export interface TenantProvider extends OAuthProviderItf {
  name: 'google'|'facebook'|'line'|'email';
  client_id: string;
  client_secret: string;
  email_is_required_for_signup?: boolean;
  allow_signup_with_password?: boolean;
  otp_digits?: number;
  otp_expiry_time?: number;
  allow_passwordless?: boolean;
};

export interface ClientProvider extends OAuthProviderItf {
  name: 'google'|'facebook'|'line'|'email';
  signup_provider_name?: string;
  signup_redirect_uri?: string;
  login_provider_name?: string;
  login_redirect_uri?: string;
  forgot_password_provider_name?: string;
  forgot_password_redirect_uri?: string;
};


export type Tenant = {
  tenant_id: string;
  name: string;
  title: string;
  state: 'enabled'|'disabled'|'removed';
  description?: string;
  providers: TenantProvider[];
  username: {
    can_signup: boolean;
    can_login: boolean;
  };
  signup_role_id?: string;
  fqdn: string;
  custom_domains: {
    fqdn: string;
    is_verified: boolean;
  }[];
  auto_link_email?: boolean;
};

export type TenantSummary = Pick<Tenant, 'tenant_id'|'name'|'title'|'state'>;

export type Role = {
  role_id: string;
  name: string;
  type: 'owner'|'internal'|'shared';
  title: string;
  state: 'enabled'|'disabled';
  description?: string;
  scopes: string[];
};

export type UserRoleBinding = {
  id: string;
  type: 'user';
  role_id: string;
  user_id: string;
  user_name: string;
  user_tenant_id: string;
};

export type TenantRoleBinding = {
  id: string;
  type: 'tenant';
  role_id: string;
  shared_tenant_id: string;
  shared_tenant_name: string;
};

export type RoleBinding = UserRoleBinding|TenantRoleBinding;

export function isUserRoleBinding(role_binding: RoleBinding): role_binding is UserRoleBinding {
  return role_binding.type === 'user';
}

export function isTenantRoleBinding(role_binding: RoleBinding): role_binding is TenantRoleBinding {
  return role_binding.type === 'tenant';
}

export type UserKey = {
  key_id: string;
  description?: string;
};

export type Member = {
  id: string;
  name: string;
  username?: string;
  owned_by_tenant?: boolean;
  email?: string;
  email_verified?: boolean;
  state: 'enabled'|'disabled';
  description?: string;
  tenant_id: string;
  keys?: UserKey[];
};

export type MemberSummary = Pick<Member, 
  |'id'|'name'|'username'|'email'|'email_verified'|'state'|'tenant_id'
>;

export type Client = {
  name: string;
  client_id: string;
  client_secret: string;
  description?: string;
  full_role_id: string;
  providers: ClientProvider[];
  is_published: boolean;
  access_token: {
    include_scope: boolean;
    expires_in: number;
  };
  refresh_token: {
    is_enabled: boolean;
    expires_in: number;
  };
  username: {
    can_signup: boolean;
    can_login: boolean;
  };
  state: 'enabled'|'disabled';
  authorized_redirect_uris: string[];
};

export type ClientSummary = Pick<Client, 'name'|'client_id'|'state'>;

export type Scope = {
  scope_id: string;
  name: string;
  title: string;
  description?: string;
};

export type ScopeSummary = Pick<Scope, 'scope_id'|'name'|'title'>;
